import { clsx } from "clsx";
import { IconHeart, IconHeartFilled } from "@tabler/icons-react";
import type { ProductVariantDto } from "../../../../api";
import { Image } from "../../../../components/common/image";
import { IconPill } from "../../../../components/common/icon-pill";
import { useWishlistUiStore } from "../../../../hooks/stores/useWishlistUiStore";

interface ProductImageGalleryProps {
  displayImage?: string | null;
  name: string;
  /** Template-level gallery images (product.image rows). Rendered before variant thumbs. */
  galleryImages?: string[];
  thumbnailVariants: ProductVariantDto[];
  padCount: number;
  isInWishlist: boolean;
  onToggleWishlist: () => void;
  onSelectVariant: (id: number) => void;
  onThumbnailClick: (image: string) => void;
}

export function ProductImageGallery({
  displayImage,
  name,
  galleryImages = [],
  thumbnailVariants,
  padCount,
  isInWishlist,
  onToggleWishlist,
  onSelectVariant,
  onThumbnailClick,
}: ProductImageGalleryProps) {
  // Reserve thumbnail slots for gallery images first, then variants, then pads.
  // Total visible row stays at 4 slots so the layout doesn't reflow.
  const playFlyToWishlist = useWishlistUiStore((s) => s.playFlyToWishlist);
  const galleryThumbs = galleryImages.slice(0, 4);
  const remaining = Math.max(0, 4 - galleryThumbs.length);
  const variantThumbs = thumbnailVariants.slice(0, remaining);
  const adjustedPadCount = Math.max(0, padCount - galleryThumbs.length);
  const padItems = Array.from({ length: adjustedPadCount });

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-bgLight">
        <Image image={displayImage} alt={name} size="w-full h-full" showPlaceholder />
        <IconPill
          as="button"
          size={36}
          tone="ghost"
          shape="circle"
          aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            if (!isInWishlist) {
              const imageEl = e.currentTarget.closest(".relative")?.querySelector(".w-full.h-full") as HTMLElement | null;
              playFlyToWishlist({
                source: imageEl ?? e.currentTarget,
                image: displayImage ?? undefined,
              });
            }
            onToggleWishlist();
          }}
          className="absolute left-4 top-4"
        >
          {isInWishlist
            ? <IconHeartFilled size={18} className="text-accentError" />
            : <IconHeart size={18} />}
        </IconPill>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {galleryThumbs.map((src, i) => {
          const isActive = src === displayImage;
          return (
            <button
              key={`gallery-${i}`}
              type="button"
              onClick={() => onThumbnailClick(src)}
              aria-pressed={isActive}
              className={clsx(
                "aspect-square overflow-hidden rounded-xl bg-bgLight transition-colors",
                isActive ? "ring-2 ring-textDark" : "ring-1 ring-borderDefault hover:ring-accentCyan",
              )}
            >
              <Image image={src} alt={name} size="w-full h-full" showPlaceholder />
            </button>
          );
        })}
        {variantThumbs.map((v) => {
          const thumbImage = v.image || displayImage;
          const isActive = thumbImage === displayImage;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => {
                onSelectVariant(v.id);
                if (thumbImage) onThumbnailClick(thumbImage);
              }}
              aria-pressed={isActive}
              className={clsx(
                "aspect-square overflow-hidden rounded-xl bg-bgLight transition-colors",
                isActive ? "ring-2 ring-textDark" : "ring-1 ring-borderDefault hover:ring-accentCyan",
              )}
            >
              <Image image={thumbImage} alt={v.name ?? ""} size="w-full h-full" showPlaceholder />
            </button>
          );
        })}
        {padItems.map((_, i) => (
          <div key={`pad-${i}`} className="aspect-square overflow-hidden rounded-xl bg-bgLight ring-1 ring-borderDefault">
            <Image image={displayImage} alt="" size="w-full h-full" showPlaceholder />
          </div>
        ))}
      </div>
    </div>
  );
}
